/**
 * The `suite` chrome variant — the legacy EMIL Insurance Suite look, as tokens (SDD-012, U-2).
 *
 * Every value here is MIRRORED from `docs/domain/legacy-handoff/legacy-chrome.tokens.json`, which
 * was extracted from the deployed legacy admin on 2026-09-15. `__tests__/suite-tokens.test.ts`
 * loads that file and asserts equality, so the variant cannot drift from the frozen input by
 * hand: if the legacy changes, re-extract the JSON first, then change this file to match.
 *
 * This is the ONE place outside `palette.ts` where hex lives. It is deliberately not tuned for
 * contrast — parity with the legacy wins during the transition, and the pairs that fail WCAG AA
 * are recorded in SDD-012 as legacy inheritances rather than corrected here. The redesign
 * (`suite → berry`) is where they get fixed.
 */

export const suiteLayout = {
  navWidth: 264,
  navWidthCollapsed: 80,
  headerHeight: 70,
  contentPadding: 30,
  minPageWidth: 992,
} as const;

export const suiteNav = {
  background: '#3f4d67',
  text: '#a9b7d0',
  brandText: '#ffffff',
  shadow: '1px 0 20px 0 #3f4d67',
  itemPadding: '7px 15px',
  itemRadius: 0,
  /** The console keeps its manifest sections; the legacy has none. Colour from the tokens file. */
  sectionLabel: '#7f8db0',
} as const;

export const suiteHeader = {
  background: '#ffffff',
  shadow: '0 1px 20px 0 rgba(63,77,103,.15)',
  iconColor: '#3f4d67',
  minHeight: 70,
} as const;

export const suitePage = {
  background: '#f4f7fa',
  paper: '#ffffff',
  rule: '#dee2e6',
  cardBorder: 'rgba(0,0,0,.125)',
  cardRadius: 4,
  cardPadding: 24,
} as const;

export const suiteTypography = {
  fontFamily: '"Open Sans", sans-serif',
  weights: [300, 400, 600],
  baseSize: 14,
  baseColor: '#888',
  headingColor: '#212529',
  headingWeight: 500,
  headingLineHeight: 1.2,
  h3: 28,
  h5: 20,
  bodyText: '#212529',
  muted: '#888',
  lineHeight: 1.5,
} as const;

export const suiteColors = {
  primary: '#04a9f5',
  /**
   * `--main-color` semantics: the tenant accent. The legacy applies it as a CSS variable on
   * `:root`; the suite theme does the same, and Phase 4 lets runtime config override it.
   */
  accentDefault: '#1dc4e9',
  navy: '#3f4d67',
  navText: '#a9b7d0',
  ink: '#212529',
  body: '#888',
  inputBorder: '#ced4da',
  inputText: '#495057',
} as const;

export const suiteControls = {
  buttonRadius: 4,
  buttonFontWeight: 400,
  inputRadius: 4,
} as const;

/** The CSS variable the legacy's `ui-utils.ts` sets from branding; the suite theme reads it. */
export const SUITE_ACCENT_VARIABLE = '--main-color';
